import React, { useState } from 'react';
import { ArrowRight, Briefcase, MapPin, Clock, Star, Quote, Award, Users, FileCheck, Upload, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

// ==================== CAREER PAGE ====================
export const Career = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', cover_letter: '' });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const openings = [
    { title: 'Senior Fundraising Associate', location: 'Remote / India', type: 'Full-time', dept: 'Operations' },
    { title: 'Investor Relations Manager', location: 'Bengaluru, India', type: 'Full-time', dept: 'Operations' },
    { title: 'Financial Analyst', location: 'Remote', type: 'Full-time', dept: 'Research' },
    { title: 'Pitch Deck Designer', location: 'Remote', type: 'Contract', dept: 'Creative' },
    { title: 'Business Development Lead - USA', location: 'Remote', type: 'Full-time', dept: 'Sales' }
  ];

  const handleApply = (job) => {
    setSelectedJob(job);
    setFormData({ name: '', email: '', phone: '', cover_letter: '' });
    setResume(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resume) {
      toast({ title: 'Resume Required', description: 'Please upload your resume', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('email', formData.email);
      fd.append('phone', formData.phone);
      fd.append('position', selectedJob.title);
      fd.append('cover_letter', formData.cover_letter);
      fd.append('resume', resume);
      await api.post('/career', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Application Submitted!', description: 'We\'ll review and get back to you soon.' });
      setSelectedJob(null);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Submission failed', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Build the Future of <span className="text-green-200">Fundraising</span></h1>
          <p className="text-xl text-green-50 leading-relaxed">Join a team that's reshaping how startups raise capital. We're hiring driven people who believe founders deserve better.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <Card className="border-2 border-gray-100 hover:border-emerald-300 transition-all"><CardContent className="p-6 text-center">
              <Award className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-bold text-lg mb-2">Meaningful Work</h3>
              <p className="text-sm text-gray-600">Help founders raise the capital they need to change the world</p>
            </CardContent></Card>
            <Card className="border-2 border-gray-100 hover:border-emerald-300 transition-all"><CardContent className="p-6 text-center">
              <Users className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-bold text-lg mb-2">Remote-First</h3>
              <p className="text-sm text-gray-600">Work from anywhere with a globally distributed team</p>
            </CardContent></Card>
            <Card className="border-2 border-gray-100 hover:border-emerald-300 transition-all"><CardContent className="p-6 text-center">
              <Briefcase className="h-10 w-10 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-bold text-lg mb-2">Growth Opportunities</h3>
              <p className="text-sm text-gray-600">Fast-track your career in venture and fundraising</p>
            </CardContent></Card>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-8">Open Positions</h2>
          <div className="space-y-4">
            {openings.map((job, idx) => (
              <Card key={idx} className="border-2 border-gray-100 hover:border-emerald-300 transition-all hover:shadow-lg cursor-pointer group" data-testid={`job-${idx}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase rounded-full mb-2">{job.dept}</span>
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors">{job.title}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{job.location}</span>
                        <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{job.type}</span>
                      </div>
                    </div>
                    <Button onClick={() => handleApply(job)} className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
                      Apply Now<ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Application Modal */}
          {selectedJob && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelectedJob(null)}>
              <Card className="max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Apply for {selectedJob.title}</h2>
                      <p className="text-sm text-gray-500 mt-1">{selectedJob.location} • {selectedJob.type}</p>
                    </div>
                    <button onClick={() => setSelectedJob(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label className="font-semibold mb-2 block">Full Name *</Label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Doe" data-testid="career-name" />
                    </div>
                    <div>
                      <Label className="font-semibold mb-2 block">Email *</Label>
                      <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" data-testid="career-email" />
                    </div>
                    <div>
                      <Label className="font-semibold mb-2 block">Phone *</Label>
                      <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91 99530 22193" data-testid="career-phone" />
                    </div>
                    <div>
                      <Label className="font-semibold mb-2 block">Resume * (PDF/DOC, max 10MB)</Label>
                      <div className="border-2 border-dashed border-emerald-300 rounded-xl p-6 text-center hover:border-emerald-500 transition-colors">
                        <input type="file" id="resume" required accept=".pdf,.doc,.docx" onChange={(e) => setResume(e.target.files[0])} className="hidden" data-testid="career-resume" />
                        <label htmlFor="resume" className="cursor-pointer">
                          <Upload className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                          {resume ? (
                            <div className="text-sm font-medium text-emerald-700">{resume.name}</div>
                          ) : (
                            <div className="text-sm text-gray-600">Click to upload resume</div>
                          )}
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label className="font-semibold mb-2 block">Cover Letter (optional)</Label>
                      <Textarea rows={4} value={formData.cover_letter} onChange={(e) => setFormData({...formData, cover_letter: e.target.value})} placeholder="Tell us why you'd be a great fit..." />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white py-6 font-bold disabled:opacity-60" data-testid="career-submit">
                      {submitting ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Submitting...</>) : 'Submit Application'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="text-center mt-12 p-8 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border-2 border-emerald-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Don't see a fit?</h3>
            <p className="text-gray-600 mb-6">We're always looking for exceptional talent. Send us your resume.</p>
            <Button onClick={() => navigate('/contact')} className="bg-gradient-to-r from-emerald-500 to-green-600 text-white">
              Get in Touch<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== REVIEWS PAGE ====================
export const Reviews = () => {
  const reviews = [
    { name: 'Rajesh Kumar', company: 'FinTech Startup', rating: 5, review: "FounderScale's honest approach was refreshing. They told us upfront what we needed to fix before raising. Closed our seed round 3 months later.", role: 'CEO' },
    { name: 'Priya Sharma', company: 'EdTech Platform', rating: 5, review: "The pitch deck refinement alone was worth it. Investors immediately understood our value prop after we worked with the FS team.", role: 'Founder' },
    { name: 'Michael Chen', company: 'SaaS B2B', rating: 5, review: "Got connected with the right Family Office investor through them. Their network is genuine, not just a name list.", role: 'Co-Founder' },
    { name: 'Anjali Mehta', company: 'D2C Brand', rating: 5, review: "24/7 availability is real. I had a midnight investor call and Arshdeep was there to help me prep. That kind of commitment is rare.", role: 'CEO' },
    { name: 'David Patel', company: 'HealthTech', rating: 5, review: "They didn't take us on initially because we weren't ready. They came back 6 months later and we raised $1.2M Series A.", role: 'Founder' },
    { name: 'Sarah Williams', company: 'CleanTech', rating: 5, review: "Best money I spent on fundraising support. Way better ROI than I expected. The financial model they built is still our gold standard.", role: 'CFO' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">What Founders <span className="text-green-200">Say</span></h1>
          <p className="text-xl text-green-50 leading-relaxed">Real stories from real founders who trusted us with their fundraising journey.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, idx) => (
              <Card key={idx} className="border-2 border-gray-100 hover:border-emerald-300 transition-all hover:shadow-xl hover:-translate-y-1" data-testid={`review-${idx}`}>
                <CardContent className="p-8">
                  <Quote className="h-8 w-8 text-emerald-300 mb-4" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic mb-6 leading-relaxed">"{review.review}"</p>
                  <div className="pt-4 border-t border-gray-100">
                    <div className="font-bold text-gray-900">{review.name}</div>
                    <div className="text-sm text-emerald-600 font-semibold">{review.role}, {review.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== CASE STUDIES ====================
export const CaseStudies = () => {
  const cases = [
    { title: 'FinTech Startup Raises $1.5M Seed Round', sector: 'Fintech', amount: '$1.5M', timeline: '4 months', summary: 'How we helped a payments startup secure their seed round from 3 Tier-1 investors.', image: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=800&h=400&fit=crop' },
    { title: 'EdTech Platform Closes Series A at $5M', sector: 'EdTech', amount: '$5M', timeline: '6 months', summary: 'From struggling pitch to closed Series A with strategic investors in education sector.', image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=400&fit=crop' },
    { title: 'SaaS Startup Bridge Round Success', sector: 'SaaS', amount: '$750K', timeline: '2 months', summary: 'Fast bridge round execution to extend runway before Series A discussions.', image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&h=400&fit=crop' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Success <span className="text-green-200">Stories</span></h1>
          <p className="text-xl text-green-50">Real founders. Real outcomes. Real capital raised.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cases.map((cs, idx) => (
              <Card key={idx} className="border-2 border-gray-100 hover:border-emerald-300 transition-all hover:shadow-xl hover:-translate-y-2 overflow-hidden group cursor-pointer" data-testid={`case-${idx}`}>
                <div className="h-48 overflow-hidden">
                  <img src={cs.image} alt={cs.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <CardContent className="p-6">
                  <div className="flex gap-2 mb-3">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">{cs.sector}</span>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">{cs.amount}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors">{cs.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{cs.summary}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500 pt-3 border-t border-gray-100">
                    <Clock className="h-4 w-4" />
                    <span>Closed in {cs.timeline}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== LEGAL PAGES ====================
const LegalPage = ({ title, sections }) => (
  <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
    <section className="py-20 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700">
      <div className="container mx-auto px-4 text-center">
        <FileCheck className="h-16 w-16 text-white mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold text-white">{title}</h1>
        <p className="text-green-50 mt-4">Last updated: December 2026</p>
      </div>
    </section>
    <section className="py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {sections.map((section, idx) => (
          <div key={idx} className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
            <div className="text-gray-700 leading-relaxed space-y-3">
              {section.content.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        ))}
        <div className="mt-12 p-6 bg-emerald-50 border-2 border-emerald-200 rounded-2xl">
          <p className="text-gray-700"><strong>Questions?</strong> Contact us at <a href="mailto:info@founderscale.capital" className="text-emerald-600 font-semibold">info@founderscale.capital</a> or call <a href="tel:+919953022193" className="text-emerald-600 font-semibold">+91 99530 22193</a></p>
        </div>
      </div>
    </section>
  </div>
);

export const Terms = () => (
  <LegalPage title="Terms & Conditions" sections={[
    { title: '1. Acceptance of Terms', content: ['By accessing and using FounderScale Capital services, you agree to be bound by these Terms and Conditions.', 'These terms govern your use of our services, including consultations, fundraising advisory, and related offerings.'] },
    { title: '2. Services', content: ['FounderScale Capital provides fundraising advisory and related services to early-stage startups.', 'We reserve the right to decline engagement if we determine that we cannot effectively serve your needs.'] },
    { title: '3. Confidentiality', content: ['All information shared with us is treated as strictly confidential. We do not publicly disclose client details, financial information, or business plans.', 'We may use anonymized aggregate data for research and case studies.'] },
    { title: '4. Fees and Payment', content: ['Service fees are agreed upon at the start of engagement and detailed in our service agreement.', 'Payment terms are specified in individual contracts.'] },
    { title: '5. Limitation of Liability', content: ['FounderScale Capital does not guarantee funding outcomes. We provide advisory services, but the actual fundraising success depends on multiple factors including market conditions and investor decisions.'] },
    { title: '6. Governing Law', content: ['These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Bengaluru, India.'] }
  ]} />
);

export const Privacy = () => (
  <LegalPage title="Privacy Policy" sections={[
    { title: '1. Information We Collect', content: ['We collect information you provide directly to us, including name, email, phone number, startup details, and other information shared during consultations.'] },
    { title: '2. How We Use Information', content: ['We use your information to provide our services, communicate with you, improve our offerings, and comply with legal obligations.', 'We do not sell or share your personal information with third parties for marketing purposes.'] },
    { title: '3. Data Security', content: ['We implement industry-standard security measures to protect your information from unauthorized access, alteration, or disclosure.'] },
    { title: '4. Cookies and Tracking', content: ['Our website uses cookies to improve user experience. You can control cookie preferences through your browser settings.'] },
    { title: '5. Third-Party Services', content: ['We may use trusted third-party services (e.g., analytics, email providers) that have their own privacy policies. We carefully select partners who maintain high privacy standards.'] },
    { title: '6. Your Rights', content: ['You have the right to access, correct, or delete your personal information. Contact us to exercise these rights.'] }
  ]} />
);

export const Refund = () => (
  <LegalPage title="Refund Policy" sections={[
    { title: '1. Service Fees', content: ['FounderScale Capital provides advisory services with various fee structures, including retainers, milestone-based payments, and success fees as detailed in individual engagement letters.'] },
    { title: '2. Refund Eligibility', content: ['Initial consultation fees are non-refundable as they cover the time invested in understanding your business.', 'Retainer fees may be partially refundable if work has not commenced, subject to a 10% administrative charge.'] },
    { title: '3. Cancellation', content: ['Either party may terminate the engagement with 30 days written notice.', 'Upon cancellation, you will be billed for work completed up to the cancellation date.'] },
    { title: '4. Success Fees', content: ['Success fees are due only upon successful closing of a funding round and are non-refundable once paid.'] },
    { title: '5. Disputes', content: ['Any disputes regarding fees or refunds should be raised within 30 days of the relevant invoice or service.', 'We will work in good faith to resolve any concerns.'] },
    { title: '6. Contact Us', content: ['For refund requests or questions, please email info@founderscale.capital with your engagement details.'] }
  ]} />
);

// ==================== FOR STARTUPS LANDING ====================
export const ForStartups = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">For <span className="text-green-200">Startups</span></h1>
          <p className="text-xl text-green-50 leading-relaxed">From ideation to pre-Series A. Everything you need to raise capital, structured for founders.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our <span className="text-emerald-600">Service Offerings</span></h2>
            <p className="text-xl text-gray-600">Pick the support that matches your stage and needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { title: 'Fundraising Assistance', desc: 'End-to-end fundraising support', path: '/services/fundraising-assistance' },
              { title: 'Investor Discovery & Mapping', desc: 'Find investors who fit you', path: '/services/investor-discovery' },
              { title: 'Investor Outreach', desc: 'Multi-channel outreach campaigns', path: '/services/investor-outreach' },
              { title: 'Data Room Creation', desc: 'Investor-ready documentation', path: '/services/data-room' },
              { title: 'Pitch Deck Creation', desc: 'Stories that resonate', path: '/services/pitch-deck' },
              { title: 'Financial Modeling', desc: 'Numbers investors trust', path: '/services/financial-modeling' }
            ].map((item, idx) => (
              <Card key={idx} className="border-2 border-gray-100 hover:border-emerald-300 transition-all hover:shadow-xl hover:-translate-y-1 cursor-pointer group" onClick={() => navigate(item.path)} data-testid={`startup-service-${idx}`}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">{item.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{item.desc}</p>
                  <span className="text-emerald-600 font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">Learn More <ArrowRight className="h-4 w-4" /></span>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">By Funding <span className="text-emerald-600">Stage</span></h2>
            <p className="text-xl text-gray-600">Tailored support for where you are right now</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Early Stage', desc: 'Ideation, Pre-Seed, Seed', path: '/stage/early' },
              { title: 'Growth Stage', desc: 'Series A & B', path: '/stage/growth' },
              { title: 'Late Stage', desc: 'Series C+ & Pre-IPO', path: '/stage/late' }
            ].map((item, idx) => (
              <Card key={idx} className="border-2 border-emerald-100 hover:border-emerald-400 hover:bg-emerald-50 transition-all hover:shadow-xl cursor-pointer group" onClick={() => navigate(item.path)}>
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.desc}</p>
                  <span className="text-emerald-600 font-semibold flex items-center justify-center gap-1 group-hover:gap-2 transition-all">Explore <ArrowRight className="h-4 w-4" /></span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-emerald-600 to-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Compare Our Service Packages</h2>
          <p className="text-xl text-green-50 mb-8">See our 3 tiered packages: Basic, Premium, and Full-Service Support</p>
          <Button size="lg" onClick={() => navigate('/services')} className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-12 py-6 font-bold">
            View Packages<ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

// ==================== FOR INVESTORS LANDING ====================
export const ForInvestors = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="relative py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">For <span className="text-green-200">Investors</span></h1>
          <p className="text-xl text-green-50 leading-relaxed">Find quality startups, build your portfolio, and access exclusive deal flow.</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How We Help <span className="text-emerald-600">Investors</span></h2>
            <p className="text-xl text-gray-600">Tailored services for different investor types and goals</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Startup Scouting', desc: 'Curated deal flow', path: '/for-investors/startup-scouting' },
              { title: 'Startup Matchmaking', desc: 'AI-powered matching', path: '/for-investors/startup-matchmaking' },
              { title: 'Fundraising Assistance', desc: 'Support portfolio companies', path: '/for-investors/fundraising-assistance' },
              { title: 'Strategic Acquisition', desc: 'M&A opportunities', path: '/for-investors/strategic-acquisition' }
            ].map((item, idx) => (
              <Card key={idx} className="border-2 border-gray-100 hover:border-emerald-300 transition-all hover:shadow-xl hover:-translate-y-1 cursor-pointer" onClick={() => navigate(item.path)}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{item.desc}</p>
                  <span className="text-emerald-600 font-semibold text-sm flex items-center gap-1">Learn More <ArrowRight className="h-4 w-4" /></span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-emerald-600 to-green-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Find Quality Deals?</h2>
          <p className="text-xl text-green-50 mb-8">Let's discuss how we can help build your portfolio.</p>
          <Button size="lg" onClick={() => navigate('/contact')} className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-12 py-6 font-bold">
            Connect With Us<ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};
